import React, { forwardRef, memo } from 'react';
import { motion } from 'framer-motion';
import { Loader, Download } from 'lucide-react';
import { cn } from '../../lib/utils';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'export';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  glow?: boolean;
  fullWidth?: boolean;
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  elevation?: 'none' | 'sm' | 'md' | 'lg';
  isActive?: boolean;
  isDisabled?: boolean;
  ariaLabel?: string;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  exportData?: any;
  exportFilename?: string;
  exportFormat?: 'csv' | 'json';
}

const variants = {
  primary: 'bg-primary hover:bg-primary-dark text-white focus:ring-primary',
  secondary: 'bg-white text-primary border border-primary hover:bg-primary/5 focus:ring-primary',
  ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 focus:ring-gray-200',
  export: 'bg-white text-primary border border-primary hover:bg-primary/5 focus:ring-primary',
};

const sizes = {
  xs: 'px-2 py-1 text-xs',
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-base',
  lg: 'px-6 py-3 text-lg',
};

const roundedStyles = {
  none: 'rounded-none',
  sm: 'rounded',
  md: 'rounded-md',
  lg: 'rounded-lg',
  full: 'rounded-full',
};

const elevationStyles = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow',
  lg: 'shadow-md',
};

export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(
    (
      {
        variant = 'primary',
        size = 'md',
        isLoading = false,
        loadingText,
        icon,
        iconPosition = 'left',
        glow = false,
        fullWidth = false,
        rounded = 'lg',
        elevation = 'none',
        isActive = false,
        isDisabled,
        ariaLabel,
        children,
        className = '',
        disabled,
        onClick,
        type = 'button',
        exportData,
        exportFilename,
        exportFormat = 'csv',
        ...props
      },
      ref,
    ) => {
      // Combine disabled states
      const isButtonDisabled = isDisabled || disabled || isLoading;

      const handleExport = () => {
        if (!exportData || !exportFilename) return;

        let content = '';
        if (exportFormat === 'csv') {
          const headers = Object.keys(exportData[0]).join(',');
          const rows = exportData.map((item: any) => Object.values(item).join(','));
          content = [headers, ...rows].join('\n');
        } else {
          content = JSON.stringify(exportData, null, 2);
        }

        const blob = new Blob([content], { type: `text/${exportFormat}` });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${exportFilename}.${exportFormat}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      };

      const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (isButtonDisabled) return;

        if (variant === 'export') {
          handleExport();
        } else if (onClick) {
          // Prevent multiple rapid clicks (debounce)
          if ((e.target as any).dataset.lastClick) {
            const lastClick = parseInt((e.target as any).dataset.lastClick);
            if (Date.now() - lastClick < 300) return; // 300ms debounce
          }

          (e.target as any).dataset.lastClick = Date.now();
          onClick(e);
        }
      };

      // Determine if button has only an icon (no text)
      const isIconOnly = icon && !children;

      return (
        <motion.button
          ref={ref}
          whileHover={!isButtonDisabled && !className?.includes('no-motion') ? { scale: 1.02 } : undefined}
          whileTap={!isButtonDisabled && !className?.includes('no-motion') ? { scale: 0.98 } : undefined}
          className={cn(
            `
          ${variants[variant]}
          ${sizes[size]}
          ${roundedStyles[rounded]}
          ${elevationStyles[elevation]}
          ${glow && !isButtonDisabled ? 'hover:shadow-glow' : ''}
          ${fullWidth ? 'w-full' : ''}
          ${isActive ? 'ring-2 ring-offset-2' : ''}
          ${isIconOnly ? 'p-2 flex items-center justify-center' : ''}
          font-medium
          transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-offset-2
          disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:opacity-50
          flex items-center justify-center
          ${variant !== 'link' ? 'border border-transparent' : ''}
        `,
            className,
          )}
          onClick={handleClick}
          disabled={isButtonDisabled}
          type={type}
          aria-label={ariaLabel}
          aria-disabled={isButtonDisabled}
          aria-busy={isLoading ? true : undefined}
          data-testid={props['data-testid'] || `button-${variant}`}
          {...props}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <Loader className="animate-spin h-5 w-5 mr-2 rtl:ml-2 rtl:mr-0" aria-hidden="true" />
              {loadingText || children || 'جارٍ التحميل...'}
            </div>
          ) : (
            <>
              {variant === 'export' && !icon && <Download className="w-4 h-4 ml-2 rtl:mr-2 rtl:ml-0 flex-shrink-0" />}
              {icon && iconPosition === 'left' && <span className="ml-2 rtl:mr-2 rtl:ml-0 flex-shrink-0">{icon}</span>}
              {children || (variant === 'export' ? (exportFormat === 'csv' ? 'تصدير CSV' : 'تصدير JSON') : '')}
              {icon && iconPosition === 'right' && <span className="mr-2 rtl:ml-2 rtl:mr-0 flex-shrink-0">{icon}</span>}
            </>
          )}
        </motion.button>
      );
    },
  ),
);

// Add display name for debugging
Button.displayName = 'Button';
